import { Component, effect, inject, signal } from '@angular/core';
import { MobileShellComponent } from './shell/mobile-shell-component';
import { AdminService } from './core/services/admin.service';
import { AuthService } from './services/AuthService';

const STORAGE_DEFAULTS: Record<string, string> = {
  'hc.onboarding.done': '0',
  'hc.activity.askedQuestion': '0',
  'hc.privacy.profileVisibility': 'anonymous',
  'hc.searchHistory': '[]',
  'hc.user.nickname': '',
};

const OBSOLETE_STORAGE_KEYS = [
  'onboardingCompleted',
  'theme'
];

const APPEARANCE_STORAGE_KEY = 'hc.appearance.preferences.v1';

interface AppearancePreferences {
  fontSize?: 'small' | 'default' | 'large';
  feedDensity?: 'compact' | 'comfortable';
  cardsLayout?: 'grid' | 'list';
  reduceAnimations?: boolean;
  highContrast?: boolean;
}

@Component({
  selector: 'app-root',
  imports: [MobileShellComponent],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {
  private readonly authService = inject(AuthService);
  private readonly adminService = inject(AdminService);
  private adminStatusRequested = false;

  protected readonly title = signal('peerliv-ui');

  constructor() {
    this.cleanupObsoleteStorageKeys();
    this.seedStorageDefaults();
    this.applyAppearancePreferences();

    effect(() => {
      const isReady = this.authService.authReady();
      const isAuthenticated = this.authService.isAuthenticated();

      if (isReady && isAuthenticated && !this.adminStatusRequested) {
        this.adminStatusRequested = true;
        this.adminService.refreshAdminStatus(true);
      }

      if (!isAuthenticated) {
        this.adminStatusRequested = false;
      }
    });
  }

  private seedStorageDefaults(): void {
    if (typeof window === 'undefined') return;

    try {
      Object.entries(STORAGE_DEFAULTS).forEach(([key, value]) => {
        if (localStorage.getItem(key) === null) {
          localStorage.setItem(key, value);
        }
      });
    } catch {
      // Ignore storage unavailability (private mode/quota/security settings).
    }
  }

  private cleanupObsoleteStorageKeys(): void {
    if (typeof window === 'undefined') return;

    try {
      OBSOLETE_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
    } catch {
      // Ignore storage unavailability (private mode/quota/security settings).
    }
  }

  private applyAppearancePreferences(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    try {
      const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
      if (!raw) return;

      const prefs = JSON.parse(raw) as AppearancePreferences;
      const html = document.documentElement;

      // Font size
      if (prefs.fontSize === 'small' || prefs.fontSize === 'default' || prefs.fontSize === 'large') {
        html.classList.remove('ui-font-small', 'ui-font-default', 'ui-font-large');
        html.classList.add(`ui-font-${prefs.fontSize}`);
      }

      // Feed density
      if (prefs.feedDensity === 'compact' || prefs.feedDensity === 'comfortable') {
        html.classList.toggle('ui-density-compact', prefs.feedDensity === 'compact');
        html.classList.toggle('ui-density-comfortable', prefs.feedDensity === 'comfortable');
      }

      // Cards layout
      if (prefs.cardsLayout === 'grid' || prefs.cardsLayout === 'list') {
        html.classList.toggle('ui-cards-grid', prefs.cardsLayout === 'grid');
        html.classList.toggle('ui-cards-list', prefs.cardsLayout === 'list');
      }

      // Accessibility
      if (typeof prefs.reduceAnimations === 'boolean') {
        html.classList.toggle('ui-reduce-motion', prefs.reduceAnimations);
      }
      if (typeof prefs.highContrast === 'boolean') {
        html.classList.toggle('ui-high-contrast', prefs.highContrast);
      }
    } catch {
      // Ignore malformed preferences.
    }
  }
}

